import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'index';
import { useDispatch } from 'react-redux';
import i18n from 'i18n-js';
import {
    Typography,
    FormControl,
    FormGroup,
    FormControlLabel,
    FormHelperText,
    Switch,
    Button,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    InputLabel,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from '@material-ui/core';
import { Formik, Form } from 'formik';
import styles from '../styles';
import restaurantDetailsStore from 'store/restaurantDetailsStore';
import { IdName, Printer, PrinterInfoStatus, PrinterServer, ReceiptType, TestPrintRequest } from 'api/models';
import agent from 'api/agent';
import { formatDateTimeGermany } from 'helpers/datetimeHelper';
import {
    Print as PrintIcon,
    Star as StarIcon,
    StarOutline as StarOutlineIcon,
    Settings as SettingsIcon,
    Delete as DeleteIcon,
} from '@material-ui/icons';
import globalStore from 'store/globalStore';
import { BasicThreeDotsMenu, ImageContainer } from 'components/shared';
import { PrinterToWorkareaModal } from '../components/PrinterToWorkareaModal';
import { ImageContainerRefProps } from 'components/shared/components/ImageContainer';
import { images } from 'assets';

export default function AdvancedSettings() {
    const classes = styles();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const categories = useAppSelector((state) => state.category.categoryDetails?.categories || []);
    const dispatch = useDispatch();
    const [servers, setServers] = useState<PrinterServer[]>([]);
    const [printerToWorkareaModal, setPrinterToWorkareaModal] = useState<{ server: PrinterServer; printer: Printer; } | null>();
    const receiptLogoRef = useRef<ImageContainerRefProps>(null);

    function getDate(date?: string) {
        if (!date) return 'N/A';
        return formatDateTimeGermany(new Date(date));
    }

    function handleTestPrintClick(type: ReceiptType, server: PrinterServer, printer: Printer) {
        const model: TestPrintRequest = {
            printerIp: printer.address,
            receiptType: type,
        };
        agent.PrinterServers.testPrint(server.id, model)
            .then((res) => {
                dispatch(globalStore.actionCreators.showToaster('success', i18n.t('orders.done')));
            })
            .catch((err) => {
                console.error(err);
                dispatch(
                    globalStore.actionCreators.showToaster('error', i18n.t('common.somethingWrong'))
                );
            });
    }

    async function updatePrinterServer(server: PrinterServer) {
        return agent.PrinterServers.update(server)
            .then((res) => {
                const newServers = servers.map(x => x.id === res.id ? res : x);
                setServers(newServers);
                dispatch(globalStore.actionCreators.showToaster('success', i18n.t('messages.updatedSuccesfully')));
            })
            .catch((err) => {
                console.error(err);
                dispatch(globalStore.actionCreators.showToaster('error', i18n.t('common.somethingWrong')));
            });
    }


    function handleToggleMain(server: PrinterServer, printer: Printer) {
        const updatedServer: PrinterServer = {
            ...server,
            printers: server.printers.map((p) => ({
                ...p,
                isMain: p.address === printer.address ? !printer.isMain : false,
            })),
        };

        updatePrinterServer(updatedServer);
    }

    function openPrinterToWorkareaModal(server: PrinterServer, printer: Printer) {
        setPrinterToWorkareaModal({ server, printer });
    }

    function handleUpdateWorkAreas(server: PrinterServer, printer: Printer, workAreas: IdName[]) {
        const updatedServer: PrinterServer = {
            ...server,
            printers: server.printers.map((p) =>
                p.address === printer.address ? { ...p, workAreas } : p
            ),
        };
        updatePrinterServer(updatedServer);
        setPrinterToWorkareaModal(null);
    }

    function handleRemovePrinter(server: PrinterServer, printer: Printer) {
        return agent.PrinterServers.removePrinter(server.id, printer.address)
            .then((res) => {
                const newServers = servers.map(x => x.id === res.id ? res : x);
                setServers(newServers);
                dispatch(globalStore.actionCreators.showToaster('success', i18n.t('messages.deletedSuccesfully')));
            })
            .catch((err) => {
                console.error(err);
                dispatch(globalStore.actionCreators.showToaster('error', i18n.t('common.somethingWrong')));
            });
    }

    function preparePrinterMenuItems(server: PrinterServer, printer: Printer) {
        let result = [
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleToggleMain(server, printer)}
            >
                {printer.isMain ? (
                    <>
                        <StarOutlineIcon color="disabled" className={classes.threeDotsItemIcon} />
                        <ListItemText primary={i18n.t('button.unsetAsMainPrinter')} />
                    </>
                ) : (
                    <>
                        <StarIcon color="disabled" className={classes.threeDotsItemIcon} />
                        <ListItemText primary={i18n.t('button.setAsMainPrinter')} />
                    </>
                )}
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => openPrinterToWorkareaModal(server, printer)}
            >
                <SettingsIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.assignWorkAreas')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleTestPrintClick(ReceiptType.Receipt, server, printer)}
            >
                <PrintIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.testPrintReceipt')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleTestPrintClick(ReceiptType.WorkareaTicket, server, printer)}
            >
                <PrintIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.testPrintKitchenTicket')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleRemovePrinter(server, printer)}
            >
                <DeleteIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.delete')} />
            </div>,
        ];

        return result;
    }

    function getServers() {
        agent.PrinterServers.getList().then((res) => {
            setServers(res);
        });
    }

    useEffect(() => {
        if (!restaurant) return;
        getServers();
    }, [restaurant]);

    useEffect(() => {
        const timeout = setInterval(() => {
            if (!restaurant) return;
            getServers();
        }, 5000);

        return () => clearInterval(timeout);
    }, []);

    return (
        <div className={classes.categoriesContainer}>
            <Box m={3} mt={0}>
                <Typography variant="h5" align="left">
                    {i18n.t('common.printingOptions')}
                </Typography>

                <Formik
                    initialValues={{
                        enablePrintingActions:
                            restaurant?.advancedSettings?.isPrintingEnabled || false,
                        orderOverviewPrintoutEnabled:
                            restaurant?.advancedSettings?.orderOverviewPrintout?.isEnabled || false,
                        printingStations:
                            restaurant?.advancedSettings?.orderOverviewPrintout?.printingStations ||
                            [],
                        receiptLogoUrl: restaurant?.advancedSettings?.receiptLogoUrl || ''
                    }}
                    enableReinitialize
                    onSubmit={async (values) => {
                        if (receiptLogoRef?.current) {
                            const newReceiptLogoUrl = await receiptLogoRef.current.uploadImage()
                                .catch(err => {
                                    dispatch(globalStore.actionCreators.showToaster('error', err));
                                    throw err;
                                });
                            values.receiptLogoUrl = newReceiptLogoUrl;
                        }
                        dispatch(
                            restaurantDetailsStore.actionCreators.updateRestaurantAdvancedSettings({
                                isPrintingEnabled: values.enablePrintingActions,
                                orderOverviewPrintout: {
                                    isEnabled: values.orderOverviewPrintoutEnabled,
                                    printingStations: values.printingStations,
                                },
                                receiptLogoUrl: values.receiptLogoUrl
                            })
                        );
                    }}
                >
                    {({ values, setFieldValue, isSubmitting, dirty }) => (
                        <Form>
                            <FormControl component="fieldset">
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                color="primary"
                                                name="enablePrintingActions"
                                                checked={values.enablePrintingActions}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'enablePrintingActions',
                                                        (e.target as HTMLInputElement).checked
                                                    )
                                                }
                                            />
                                        }
                                        label={i18n.t('common.enablePrintingActions')}
                                    />
                                </FormGroup>
                                <FormHelperText>
                                    {i18n.t('common.printingOptionsExplanation')}
                                </FormHelperText>
                                <br />
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                color="primary"
                                                name="orderOverviewPrintoutEnabled"
                                                checked={values.orderOverviewPrintoutEnabled}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'orderOverviewPrintoutEnabled',
                                                        (e.target as HTMLInputElement).checked
                                                    )
                                                }
                                            />
                                        }
                                        label={i18n.t('common.orderOverviewPrintout')}
                                    />
                                </FormGroup>

                                <FormControl
                                    variant="filled"
                                    style={{ marginTop: 16, marginBottom: 8 }}
                                    disabled={!values.orderOverviewPrintoutEnabled}
                                >
                                    <InputLabel id="printing-stations-label">
                                        {i18n.t('common.printingStation')}
                                    </InputLabel>
                                    <Select
                                        labelId="printing-stations-label"
                                        multiple
                                        value={values.printingStations}
                                        onChange={(e) =>
                                            setFieldValue('printingStations', e.target.value)
                                        }
                                        renderValue={(selected) =>
                                            categories
                                                .filter((category) =>
                                                    (selected as string[]).includes(category.id)
                                                )
                                                .map((category) => category.name)
                                                .join(', ')
                                        }
                                        disabled={!values.orderOverviewPrintoutEnabled}
                                    >
                                        {categories.map((category) => (
                                            <MenuItem key={category.id} value={category.id}>
                                                <Checkbox
                                                    checked={values.printingStations.includes(
                                                        category.id
                                                    )}
                                                />
                                                <ListItemText primary={category.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Box mt={3}>
                                    <Typography variant={'h6'} gutterBottom>
                                        {i18n.t('common.receiptLogo')}
                                    </Typography>
                                    <ImageContainer
                                        ref={receiptLogoRef}
                                        photo={restaurant.advancedSettings?.receiptLogoUrl}
                                        onChange={(_selected, fileUrl) => {
                                            setFieldValue('receiptLogoUrl', fileUrl);
                                        }}
                                        styles={{ marginBottom: 0 }}
                                        text={i18n.t('restaurant.uploadLogo').toUpperCase()}
                                    />
                                </Box>
                            </FormControl>

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting || !dirty}
                                className={classes.advancedSettingsSaveButton}
                            >
                                {i18n.t('button.save')}
                            </Button>
                        </Form>
                    )}
                </Formik>
            </Box>

            <Box m={3} mt={0}>
                <Typography variant={'h6'} gutterBottom>
                    {i18n.t('common.printerServers')}
                </Typography>
            </Box>

            <TableContainer className={classes.tableBody}>
                <Table stickyHeader className={classes.table} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>{i18n.t('common.status')}</TableCell>
                            <TableCell>{i18n.t('common.lastSeen')}</TableCell>
                            <TableCell>{i18n.t('common.osVersion')}</TableCell>
                            <TableCell>{i18n.t('common.printers')}</TableCell>
                            <TableCell align="right" style={{ paddingRight: 8 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {servers.length === 0 && (
                            <Box m={3}>
                                <Typography variant="body2" color="textSecondary">
                                    {i18n.t('common.noServersAttached')}
                                </Typography>
                            </Box>
                        )}
                        {servers.length > 0 &&
                            servers.map((server, i) => (
                                <React.Fragment key={server.id}>
                                    <TableRow hover>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>
                                            {server.isOnline
                                                ? i18n.t('common.online')
                                                : i18n.t('common.offline')}
                                        </TableCell>
                                        <TableCell>{getDate(server.lastSeenUtc)}</TableCell>
                                        <TableCell>{server.osVersion}</TableCell>
                                        <TableCell>{server.printers.length}</TableCell>
                                        <TableCell align="right">
                                            {/* Actions go there */}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow hover={true}>
                                        <TableCell colSpan={1}></TableCell>
                                        <TableCell colSpan={1}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                {i18n.t('common.printers')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell colSpan={4} padding={'none'}>
                                            <Box m={2}>
                                                <Table size="small">
                                                    {server.printers.length > 0 && (
                                                        <TableHead>
                                                            <TableRow hover={true}>
                                                                <TableCell>
                                                                    {i18n.t(
                                                                        'common.printerIPAddress'
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {i18n.t('common.printerModel')}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {i18n.t('common.status')}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {i18n.t('restaurant.workAreas')}
                                                                </TableCell>
                                                                <TableCell align="right"></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                    )}
                                                    <TableBody>
                                                        {server.printers.length === 0 && (
                                                            <TableCell>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textSecondary"
                                                                >
                                                                    {i18n.t(
                                                                        'common.noPrintersAttached'
                                                                    )}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        {server.printers.map((printer) => (
                                                            <TableRow
                                                                key={printer.address}
                                                                hover={true}
                                                            >
                                                                <TableCell width={250}>
                                                                    <Box display="flex" alignItems="center">
                                                                        <Typography variant="body2">
                                                                            {printer.address}
                                                                        </Typography>
                                                                        {printer.isMain && (
                                                                            <Box ml={1}>
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={i18n.t('common.isMain').toUpperCase()}
                                                                                    color="primary"
                                                                                />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {printer.model}
                                                                </TableCell>
                                                                 <TableCell width={140}>
                                                                    <Box display="flex" alignItems="center">
                                                                        {i18n.t(`printerInfoStatus.${PrinterInfoStatus[printer.printerStatus]?.toLowerCase()}`)}
                                                                        {
                                                                            printer.printerStatus === PrinterInfoStatus.OutOfPaper &&
                                                                            <img alt="warning" src={images.icons.warning} className={classes.mLeft10}/>
                                                                        }
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {printer.workAreas.map(x => x.name).join(", ")}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <BasicThreeDotsMenu
                                                                        items={preparePrinterMenuItems(server, printer)}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {printerToWorkareaModal && (
                <PrinterToWorkareaModal
                    open={printerToWorkareaModal !== null}
                    onClose={() => setPrinterToWorkareaModal(null)}
                    printer={printerToWorkareaModal.printer}
                    categories={categories}
                    onSave={(workAreas) => handleUpdateWorkAreas(printerToWorkareaModal.server, printerToWorkareaModal.printer, workAreas)}
                />
            )}
        </div>
    );
}
